/**
 * Server times, read the way the server writes them. Every drip, Verity and drip-template screen reads
 * its times through this file.
 *
 * The server stores naive UTC ISO strings - datetime.utcnow().isoformat(), "2026-10-09T09:15:00.123456",
 * with no Z and no offset - and compares them as strings, so that format stays. A browser reads a
 * date-time without an offset as LOCAL time: the 02:15 Pacific sweep showed as 9:15 AM. Here a string
 * with no Z and no +hh:mm / -hh:mm offset is UTC; one that has either is read as it says.
 *
 * A date on its own (YYYY-MM-DD: lastSentDate, a welcomedOutside date, any Pacific day the server
 * already worked out) is a day, not a moment, and is never shifted.
 *
 * No imports, so it can be checked on its own (see the pull request that added it).
 */

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;
const HAS_OFFSET = /(?:[zZ]|[+-]\d{2}:?\d{2})$/;

// The drip's own day (DRIP_SEND_TIMEZONE on the server). formatToParts, not a locale's date format,
// so the result is YYYY-MM-DD whatever the browser's locale data says.
const PACIFIC_DAY = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit',
});

/** The moment a server time names, or null: empty, a date on its own, or unreadable. */
export function parseServerTime(value?: string | null): Date | null {
  if (typeof value !== 'string') return null;
  const raw = value.trim();
  if (!raw || DATE_ONLY.test(raw)) return null;
  // SendGrid's updated_at separates the date and the time with a space.
  const iso = raw.includes('T') ? raw : raw.replace(' ', 'T');
  const d = new Date(HAS_OFFSET.test(iso) ? iso : `${iso}Z`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** A moment as the viewer's local date and time, e.g. "Oct 9, 2026, 2:15 AM". */
export function formatMoment(d: Date): string {
  return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * A server time as the viewer's local date and time; `empty` when there is none, and the text itself
 * when it is not a moment (a date on its own, or something unreadable), so nothing is ever hidden.
 */
export function formatServerTime(value?: string | null, empty = '—'): string {
  if (value === null || value === undefined || String(value).trim() === '') return empty;
  const d = parseServerTime(value);
  return d ? formatMoment(d) : String(value);
}

/**
 * The Pacific calendar day (YYYY-MM-DD) of a server time - the day the drip counts in
 * (utils/drip_engine.local_date) - for a cell that labels a day. A date on its own is that day; ''
 * when it cannot be read.
 */
export function pacificDay(value?: string | null): string {
  if (typeof value !== 'string') return '';
  const raw = value.trim();
  if (DATE_ONLY.test(raw)) return raw;
  const d = parseServerTime(raw);
  if (!d) return '';
  const p: Record<string, string> = {};
  for (const part of PACIFIC_DAY.formatToParts(d)) p[part.type] = part.value;
  return `${p.year}-${p.month}-${p.day}`;
}
